import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SectionTitle from './shared/SectionTitle';

const ImageContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',
    minHeight: '320px',
    position: 'relative',
    borderRadius: theme.spacing(1.5),
    overflow: 'hidden',
    border: '2px solid #1a3a5c',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    [theme.breakpoints.down('md')]: {
        minHeight: '240px',
    }
}));

const StyledImage = styled('img')({
    width: '100%',
    height: 'auto',
    objectFit: 'cover',
    display: 'block',
});

const ContentContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(3, 0),
    }
}));

const CredentialItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    '&:last-child': {
        marginBottom: 0,
    },
}));

const ShowcaseGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(3),
    marginTop: theme.spacing(5),
    maxWidth: '900px',
    margin: '0 auto',
    justifyItems: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        gap: theme.spacing(2),
        marginTop: theme.spacing(4),
    },
}));

const ShowcaseImage = styled('img')(({ theme }) => ({
    height: '400px',
    width: 'auto',
    maxWidth: '100%',
    display: 'block',
    borderRadius: theme.spacing(1.5),
    border: '2px solid #1a3a5c',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    [theme.breakpoints.down('md')]: {
        height: '300px',
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: 'auto',
    },
}));

const credentials = [
    'Over 30 years of sail design experience since 1991',
    'Built sails for the 1992 Summer Olympics (North Sails One Design)',
    "Sailmaker for the Pact '95 America's Cup Team",
    "Former manager of Hobie Cat's sail loft",
    'Personally involved in every Point Sails design'
];

const DesignerBioSection: React.FC = () => {
    return (
        <Box>
            <SectionTitle centered sx={{ mb: 2 }}>
                Meet Your Sail Designer
            </SectionTitle>

            <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={6}>
                    <ImageContainer>
                        <StyledImage
                            src="/images/ChipR.jpg"
                            alt="Chip Buck - Lead Sail Designer at Point Sails"
                            loading="lazy"
                            decoding="async"
                        />
                    </ImageContainer>
                </Grid>

                <Grid item xs={12} md={6}>
                    <ContentContainer>
                        <Typography 
                            variant="h4" 
                            gutterBottom
                            sx={{ 
                                fontWeight: 600,
                                color: 'primary.main',
                                mb: 1
                            }}
                        >
                            Chip Buck
                        </Typography>
                        <Typography 
                            variant="h6" 
                            gutterBottom
                            sx={{ 
                                fontWeight: 500,
                                color: 'text.secondary',
                                mb: 3
                            }}
                        >
                            Founder & Lead Sail Designer
                        </Typography>

                        <Typography 
                            variant="body1"
                            sx={{ 
                                fontSize: '1rem',
                                lineHeight: 1.7,
                                color: 'text.secondary',
                                mb: 3
                            }}
                        >
                            Chip has been designing sails since 1991, starting with windsurfing sails 
                            and evolving through work with world-class sailing programs. He's worked 
                            with cruisers, club racers, and offshore sailors on a wide range of boats, 
                            from pocket cruisers to performance cruisers and race boats. His focus is 
                            simple: durable, efficient sail shapes that match how you actually use your boat.
                        </Typography>

                        <Box>
                            {credentials.map((credential, index) => (
                                <CredentialItem key={index}>
                                    <CheckCircleIcon 
                                        sx={{ 
                                            color: 'success.main',
                                            fontSize: '1.25rem',
                                            flexShrink: 0,
                                            mt: 0.25
                                        }} 
                                    />
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            fontSize: '0.938rem',
                                            lineHeight: 1.5,
                                            color: 'text.primary'
                                        }}
                                    >
                                        {credential}
                                    </Typography>
                                </CredentialItem>
                            ))}
                        </Box>

                        <Box 
                            sx={{ 
                                mt: 3,
                                pt: 3,
                                borderTop: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            <Typography 
                                variant="body1"
                                sx={{ 
                                    fontStyle: 'italic',
                                    fontSize: '1rem',
                                    lineHeight: 1.6,
                                    color: 'primary.main'
                                }}
                            >
                                "A good sail isn't just about cloth—it's about shape, balance, 
                                and how you actually use your boat. I design with that in mind 
                                for every sail we build."
                            </Typography>
                        </Box>
                    </ContentContainer>
                </Grid>
            </Grid>

            {/* Showcase images - Full width below bio */}
            <ShowcaseGrid>
                <ShowcaseImage
                    src="/images/AmSailZ.jpg"
                    alt="Sail construction on the loft floor"
                    loading="lazy"
                />
                <ShowcaseImage
                    src="/images/AmericaZ.jpg"
                    alt="Sail under power on a schooner"
                    loading="lazy"
                />
            </ShowcaseGrid>
            
            <Typography 
                variant="body2"
                align="center"
                sx={{ 
                    mt: 2,
                    fontSize: '0.875rem',
                    color: 'text.secondary'
                }}
            >
                From small cruisers to classic schooners
            </Typography>
        </Box>
    );
};

export default DesignerBioSection;
